import session from '@ep/tools/session';
import storage from '@ep/tools/storage';
import ajax from '@ep/tools/ajax';
import authClient from '@ep/auth-client/client';
import {
  getProductOrigin,
  inferProductFromHost,
  normalizeAppEnv,
} from '@ep/config/domainMap';
import userDefaultPhoto from '@EPImages/user.png';
import renderPlatformNotFound from '@ep/tools/notFound';
import Loader from '@ep/tools/loader';
import {
  applyResolvedVisualTheme,
  applyVisualPreferences,
  normalizeVisualScale,
  readVisualPreferences,
} from '@ep/tools/visualPreferences';

const DEFAULT_LOCALE = 'pt-BR';
const LOCALE_PATTERN = /^([a-z]{2})-([a-z]{2})$/i;
const runtimeEnv = typeof process !== 'undefined' && process?.env ? process.env : {};
const isProdRuntime = String(runtimeEnv.APP_ENV || runtimeEnv.NODE_ENV || '').trim().toLowerCase() === 'prod'
  || String(runtimeEnv.NODE_ENV || '').trim().toLowerCase() === 'production';
const SSO_PROBE_COOLDOWN_MS = 15000;
const AUTH_STATE_STORAGE_KEY = 'ep:ecosystem-auth-state';
const AUTH_SYNC_RUNTIME_KEY = Symbol.for('nucleop.ep.auth-sync-runtime');

const normalizeLocale = (value) => {
  if (typeof value !== 'string') return null;
  const match = value.trim().replace('_', '-').match(LOCALE_PATTERN);
  if (!match) return null;
  return `${match[1].toLowerCase()}-${match[2].toUpperCase()}`;
};

const normalizeSupportedLocales = (supported, fallback = DEFAULT_LOCALE) => {
  const normalizedFallback = normalizeLocale(fallback) || DEFAULT_LOCALE;
  const locales = Array.isArray(supported)
    ? supported.map(normalizeLocale).filter(Boolean)
    : [];
  return locales.length > 0 ? locales : [normalizedFallback];
};

const buildDictionaryModules = (dictionaryContext) => {
  return dictionaryContext.keys().reduce((acc, key) => {
    const match = key.match(/^\.\/([^/]+)\/([^/]+)\.js$/);
    if (!match) {
      return acc;
    }

    const [, lang, name] = match;
    if (!acc[lang]) {
      acc[lang] = {};
    }

    acc[lang][name] = () => Promise.resolve(dictionaryContext(key));
    return acc;
  }, {});
};

const isPlainObject = (value) => (
  value !== null
  && typeof value === 'object'
  && !Array.isArray(value)
);

const REQUIRED_APP_CONFIG_KEYS = ['product', 'theme', 'routing', 'language', 'page', 'host'];

export const isValidAppConfig = (config) => (
  isPlainObject(config)
  && REQUIRED_APP_CONFIG_KEYS.every((key) => Object.prototype.hasOwnProperty.call(config, key))
  && isPlainObject(config.theme)
  && isPlainObject(config.routing)
  && isPlainObject(config.language)
  && isPlainObject(config.page)
  && isPlainObject(config.host)
  && typeof config.product === 'string'
  && config.product.trim().length > 0
);

export const isValidRuntimeConfig = isValidAppConfig;

export const getAppConfig = () => (
  typeof window !== 'undefined' && isValidAppConfig(window?.config)
    ? window.config
    : null
);

const mergeDictionaryValues = (coreValue, productValue) => {
  if (!isPlainObject(coreValue) || !isPlainObject(productValue)) {
    return productValue ?? coreValue;
  }

  const merged = { ...coreValue };
  for (const [key, value] of Object.entries(productValue)) {
    merged[key] = mergeDictionaryValues(coreValue[key], value);
  }
  return merged;
};

const CORE_DICTIONARY_MODULES = buildDictionaryModules(
  import.meta.webpackContext('../dictionary/', {
    recursive: true,
    regExp: /\.js$/,
  })
);

const PRODUCT_DICTIONARY_MODULES = buildDictionaryModules(
  import.meta.webpackContext('@ProductDictionary/', {
    recursive: true,
    regExp: /\.js$/,
  })
);

const NOT_FOUND_FALLBACK_LABELS = {
  'pt-BR': {
    notFoundPage: 'A pagina que voce esta procurando nao existe.',
    notFoundPage2: 'Voce pode ter digitado incorretamente o endereco ou a pagina pode ter sido removida.',
    notFoundPage3: 'Ir para a pagina principal',
  },
};
const APP_PAGE_MODULES = import.meta.webpackContext('@AppPages/', {
  recursive: true,
  regExp: /\.js$/,
});
const APP_PAGE_KEYS = new Set(APP_PAGE_MODULES.keys());
const createAppPageLoader = (key, page) => async () => {
  try {
    return await APP_PAGE_MODULES(key);
  } catch (error) {
    console.warn(`Falha ao carregar modulo de pagina "${page}.js":`, error?.message || error);
    return null;
  }
};

const PAGE_LOADERS = {};
const REQUIRED_SHELL_PAGES = new Set(['logged', 'unlogged']);
APP_PAGE_KEYS.forEach((key) => {
  const match = key.match(/^\.\/([^/]+)\.js$/);
  if (!match) return;

  const page = match[1];
  const isDevelopmentPage = !isProdRuntime && page === 'tutorial';
  if (!REQUIRED_SHELL_PAGES.has(page) && !isDevelopmentPage) return;

  PAGE_LOADERS[page] = createAppPageLoader(key, page);
});

const THEME_TOKENS = ['dark', 'light', 'system'];
const THEME_NAME_PATTERN = /^(?!.*\/\/)(?!.*\.\.)[a-z0-9]+(?:[/-][a-z0-9]+)*$/;
const LEGACY_THEME_NAME_MAP = {
  'dark/gray-01': 'dark/gray-goldenrod-01',
};

const normalizeThemeToken = (token) => {
  if (typeof token !== 'string') return null;
  const normalized = token.trim().toLowerCase();
  return THEME_TOKENS.includes(normalized) ? normalized : null;
};

const resolveThemeSettings = (config = {}) => {
  const resolvedConfig = isValidAppConfig(config) ? config : {};
  const rawTheme = resolvedConfig?.theme;

  if (rawTheme && typeof rawTheme === 'object' && !Array.isArray(rawTheme)) {
    const mode = normalizeThemeToken(rawTheme.mode) || 'light';
    const canChange = rawTheme.canChange ?? resolvedConfig?.changeTheme;
    const lightName = String(rawTheme.light || 'light/blue-goldenrod-01').trim().toLowerCase();
    const darkName = String(rawTheme.dark || 'dark/gray-goldenrod-01').trim().toLowerCase();
    return {
      mode,
      canChange: Boolean(canChange),
      lightName,
      darkName,
    };
  }

  return {
    mode: normalizeThemeToken(rawTheme) || 'light',
    canChange: Boolean(resolvedConfig?.changeTheme),
    lightName: 'light/blue-goldenrod-01',
    darkName: 'dark/gray-goldenrod-01',
  };
};

const normalizeThemeName = (name, fallback) => {
  const normalized = String(name || '').trim().toLowerCase();
  if (!THEME_NAME_PATTERN.test(normalized)) {
    return fallback;
  }
  return LEGACY_THEME_NAME_MAP[normalized] || normalized;
};

const resolveUserPhotoURL = (photoURL) => {
  if (typeof photoURL !== 'string') {
    return userDefaultPhoto;
  }

  const normalized = photoURL.trim();
  const lowered = normalized.toLowerCase();
  if (
    !normalized
    || lowered === 'null'
    || lowered === 'undefined'
    || lowered.includes('/avatar/default')
  ) {
    return userDefaultPhoto;
  }

  if (!/^https?:\/\//i.test(normalized) && !normalized.startsWith('/') && !normalized.startsWith('@')) {
    return `/private-files/${normalized.replace(/^\/+/, '').split('/').map(encodeURIComponent).join('/')}`;
  }

  return normalized;
};

const resolveRoutingConfig = (config = {}) => {
  if (config?.routing && typeof config.routing === 'object') return config.routing;
  if (config?.branding && typeof config.branding === 'object') return config.branding;
  return {};
};

const normalizeRoutingRoutes = (routingConfig) => {
  if (Array.isArray(routingConfig?.routes)) return routingConfig.routes;
  if (routingConfig?.routes && typeof routingConfig.routes === 'object') {
    return Object.entries(routingConfig.routes).map(([key, route]) => ({
      key,
      ...(route && typeof route === 'object' ? route : {}),
    }));
  }
  return [];
};

const collectPublicRouteSlugs = (routingConfig) => {
  const routes = normalizeRoutingRoutes(routingConfig);
  const slugs = new Set();

  routes.forEach((route) => {
    const slug = typeof route?.slug === 'string' ? route.slug.trim().toLowerCase() : '';
    if (slug) slugs.add(slug);

    if (Array.isArray(route?.aliases)) {
      route.aliases.forEach((alias) => {
        const normalized = typeof alias === 'string' ? alias.trim().toLowerCase() : '';
        if (normalized) slugs.add(normalized);
      });
    }
  });

  return slugs;
};

/**
 * Controla inicialização global da aplicação (idioma, tema e carregamento de páginas).
 */
export default class Common {
  #keyboardShortcutListener = null;
  #hasCompletedFirstRender = false;
  #authSyncTimer = null;
  #authSyncInFlight = null;
  #authFocusListener = null;
  #authVisibilityListener = null;

  #normalizeSsoReturnPath(returnPath, targetOrigin) {
    try {
      const target = new URL(targetOrigin);
      const destination = new URL(String(returnPath || '/'), target);
      if (destination.origin !== target.origin) return '/';
      return this.#stripLegacySsoParams(destination);
    } catch {
      return '/';
    }
  }

  #stripLegacySsoParams(urlLike) {
    try {
      const normalized = urlLike instanceof URL ? new URL(urlLike.toString()) : new URL(String(urlLike || '/'), 'http://localhost');
      [
        'sso_broker',
        'sso_target',
        'sso_return',
        'sso_candidates',
        'sso_source',
        'sso_checked',
      ].forEach((key) => normalized.searchParams.delete(key));
      return `${normalized.pathname}${normalized.search}${normalized.hash}` || '/';
    } catch {
      return '/';
    }
  }

  #resolveAppEnv() {
    return normalizeAppEnv(runtimeEnv.APP_ENV || runtimeEnv.NODE_ENV || 'dev');
  }

  #resolveCurrentProductCode() {
    const fallback = String(
      runtimeEnv.AUTH_PRODUCT_CODE
      || runtimeEnv.PRODUCT_NAME
      || runtimeEnv.APP_PRODUCT
      || 'platform'
    ).trim().toLowerCase();

    if (typeof window === 'undefined') {
      return fallback || 'platform';
    }

    return inferProductFromHost(window.location?.hostname || '') || fallback || 'platform';
  }

  #getSsoProbeStorageKey(productCode = null) {
    const product = String(productCode || this.#resolveCurrentProductCode() || 'platform').trim().toLowerCase();
    return `ep:sso-probe:${product}`;
  }

  #readEcosystemAuthState() {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(AUTH_STATE_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  #writeEcosystemAuthState(state, productCode = null) {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(AUTH_STATE_STORAGE_KEY, JSON.stringify({
        state,
        productCode: String(productCode || this.#resolveCurrentProductCode() || '').trim().toLowerCase(),
        timestamp: Date.now(),
      }));
    } catch {}
  }

  #shouldProbeEcosystemAuth(productCode = null) {
    const authState = this.#readEcosystemAuthState();
    if (!authState || authState.state !== 'authenticated') return false;
    const timestamp = Number(authState.timestamp);
    if (!Number.isFinite(timestamp)) return false;
    if ((Date.now() - timestamp) > (12 * 60 * 60 * 1000)) return false;
    const currentProduct = String(productCode || this.#resolveCurrentProductCode() || '').trim().toLowerCase();
    return String(authState.productCode || '').trim().toLowerCase() !== currentProduct;
  }

  #markSsoProbe(productCode = null) {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(this.#getSsoProbeStorageKey(productCode), String(Date.now()));
    } catch {}
  }

  #clearSsoProbe(productCode = null) {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(this.#getSsoProbeStorageKey(productCode));
    } catch {}
  }

  #hasRecentSsoProbe(productCode = null) {
    if (typeof window === 'undefined') return false;
    try {
      const raw = window.sessionStorage.getItem(this.#getSsoProbeStorageKey(productCode));
      const timestamp = Number(raw);
      return Number.isFinite(timestamp) && (Date.now() - timestamp) < SSO_PROBE_COOLDOWN_MS;
    } catch {
      return false;
    }
  }

  #cleanSsoGrantFromUrl() {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('sso_grant')) return;
    url.searchParams.delete('sso_grant');
    url.searchParams.delete('remember');
    url.searchParams.delete('source_product');
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', next);
  }

  #consumeSsoProbeFlagFromUrl() {
    if (typeof window === 'undefined') return false;
    const url = new URL(window.location.href);
    const checked = String(url.searchParams.get('sso_checked') || '').trim() === '1';
    if (!checked) return false;

    this.#markSsoProbe();

    url.searchParams.delete('sso_checked');
    url.searchParams.delete('sso_source');
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', next);
    return true;
  }

  async #consumeSsoGrantFromUrl() {
    this.#cleanSsoGrantFromUrl();
    return null;
  }

  async #getLocalProductSessionData(productCode = null, {
    suppressErrors = true,
    forceRefresh = false,
  } = {}) {
    const product = String(productCode || this.#resolveCurrentProductCode() || '').trim().toLowerCase();
    if (!product || product === 'site') return null;

    try {
      return await ajax.get({
        product,
        category: 'core',
        domain: 'core',
        module: 'master',
        method: 'getSessionData',
        ...(forceRefresh ? { query: { refresh: '1' } } : {}),
        config: { loader: false, retries: 3, retryDelay: 250 },
      });
    } catch (error) {
      if (!suppressErrors) throw error;
      return null;
    }
  }

  #isTransientSessionValidationFailure(error) {
    const status = Number(error?.status || error?.response?.status || 0);
    const message = String(error?.message || '').toLowerCase();

    if ([401, 403, 419].includes(status)) return false;

    return (
      status >= 500
      || error instanceof TypeError
      || message.includes('failed to fetch')
      || message.includes('proxy falhou ao encaminhar')
      || message.includes('trying to proxy')
      || message.includes('bad gateway')
      || message.includes('econnreset')
      || message.includes('econnrefused')
      || message.includes('socket hang up')
    );
  }

  async #bootstrapProductSessionFromEcosystemAuth() {
    return false;
  }

  async goToProduct(targetProductCode, {
    path = '/',
    replace = false,
  } = {}) {
    if (typeof window === 'undefined') return false;

    const targetProduct = String(targetProductCode || '').trim().toLowerCase();
    if (!targetProduct) return false;

    const targetOrigin = getProductOrigin(targetProduct, this.#resolveAppEnv());
    if (!targetOrigin) return false;

    const destination = new URL(this.#normalizeSsoReturnPath(path, targetOrigin), targetOrigin);
    const currentOrigin = String(window.location.origin || '').toLowerCase();
    if (currentOrigin === String(targetOrigin).toLowerCase()) {
      if (replace) window.location.replace(destination.toString());
      else window.location.assign(destination.toString());
      return true;
    }

    if (replace) window.location.replace(destination.toString());
    else window.location.assign(destination.toString());
    return true;
  }

  #consumeRedirectAuthToken() {
    if (typeof window === 'undefined') return null;
    const token = String(window.history.state?.epAuthToken || '').trim();
    if (!token) return null;

    session.set('token', token);
    const nextState = { ...(window.history.state || {}) };
    delete nextState.epAuthToken;
    window.history.replaceState(nextState, '', window.location.href);
    return token;
  }

  async #refreshSessionAfterPublicBoot(config, activeLang, shouldNavigateToLogged = true) {
    try {
      const currentProduct = this.#resolveCurrentProductCode();
      const sessionData = currentProduct && currentProduct !== 'site'
        ? await this.#getLocalProductSessionData(currentProduct, { suppressErrors: false })
        : await authClient.getSessionData({ loader: false, retries: 1, retryDelay: 100 });
      const isAuthenticated = !!(sessionData?.authenticated || sessionData?.token);
      if (!isAuthenticated) {
        return;
      }

      const profile = sessionData?.profile || {};
      const defaultConfigLang = normalizeLocale(config?.language?.default) || DEFAULT_LOCALE;
      const nextLang = activeLang || profile.language || (session.get('language') || defaultConfigLang);
      if (sessionData?.token) {
        session.set('token', sessionData.token);
      }
      session.set('user', profile.nickname || null);
      session.set('userPhotoURL', resolveUserPhotoURL(profile?.photoURL));

      if (shouldNavigateToLogged) {
        window.history.replaceState({}, '', '/');
        await this.initializePage(nextLang, config?.page?.logged || 'logged', null);
      }
    } catch (error) {
      console.warn('Falha ao atualizar sessão após boot público:', error);
    }
  }

  #teardownAuthSync() {
    if (typeof window === 'undefined') return;

    const runtime = window[AUTH_SYNC_RUNTIME_KEY];
    if (runtime?.timer) {
      window.clearInterval(runtime.timer);
    }
    if (runtime?.focusListener) {
      window.removeEventListener('focus', runtime.focusListener);
    }
    if (runtime?.visibilityListener) {
      document.removeEventListener('visibilitychange', runtime.visibilityListener);
    }
    delete window[AUTH_SYNC_RUNTIME_KEY];

    if (this.#authSyncTimer && this.#authSyncTimer !== runtime?.timer) {
      window.clearInterval(this.#authSyncTimer);
    }
    if (this.#authFocusListener && this.#authFocusListener !== runtime?.focusListener) {
      window.removeEventListener('focus', this.#authFocusListener);
    }
    if (this.#authVisibilityListener && this.#authVisibilityListener !== runtime?.visibilityListener) {
      document.removeEventListener('visibilitychange', this.#authVisibilityListener);
    }

    this.#authSyncTimer = null;
    this.#authFocusListener = null;
    this.#authVisibilityListener = null;
  }

  async #syncDetectedTimezone(currentProduct, sessionData) {
    if (!sessionData?.authenticated && !sessionData?.token) return sessionData;
    if (!currentProduct || currentProduct === 'site') return sessionData;

    let detectedTimezone = '';
    try {
      detectedTimezone = String(
        Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      ).trim();
      if (!detectedTimezone) return sessionData;
      new Intl.DateTimeFormat('en-US', { timeZone: detectedTimezone }).format();
    } catch {
      return sessionData;
    }

    const currentTimezone = String(sessionData?.profile?.timezone || '').trim();
    const sessionTimezone = String(session.get('timezone') || '').trim();
    if (currentTimezone === detectedTimezone && sessionTimezone === detectedTimezone) {
      return sessionData;
    }

    try {
      const response = await ajax.post({
        product: currentProduct,
        category: 'security',
        domain: 'identity',
        module: 'auth',
        method: 'syncTimezone',
        body: { timezone: detectedTimezone },
        config: { loader: false, retries: 1, retryDelay: 100 },
      });
      const timezone = String(response?.timezone || detectedTimezone).trim();
      session.set('timezone', timezone);
      return {
        ...sessionData,
        profile: {
          ...(sessionData?.profile || {}),
          timezone,
        },
      };
    } catch (error) {
      console.warn('Falha ao sincronizar fuso horário detectado:', error?.message || error);
      return sessionData;
    }
  }

  async #syncAuthState(config, loggedPage, unloggedPage) {
    if (this.#authSyncInFlight) return this.#authSyncInFlight;
    if (typeof window === 'undefined') return null;

    const hasLocalToken = !!String(session.get('token') || '').trim();
    const isLoggedShell = document.body.classList.contains('lgd');
    if (!hasLocalToken && !isLoggedShell) return null;

    const currentProduct = this.#resolveCurrentProductCode();
    this.#authSyncInFlight = (async () => {
      try {
        let sessionData = currentProduct && currentProduct !== 'site'
          ? await this.#getLocalProductSessionData(currentProduct, {
            suppressErrors: false,
            forceRefresh: true,
          })
          : await authClient.getSessionData({ loader: false, retries: 1, retryDelay: 100 });

        sessionData = await this.#syncDetectedTimezone(currentProduct, sessionData);

        if (isLoggedShell && Array.isArray(sessionData?.menus)) {
          try {
            const { syncMenuIcons } = await import('../components/create/menu');
            syncMenuIcons(sessionData.menus);
          } catch (error) {
            console.warn('Falha ao atualizar ícones do menu:', error?.message || error);
          }
        }

        if (sessionData?.token) {
          session.set('token', sessionData.token);
        }
        const profile = sessionData?.profile || {};
        const nickname = String(profile.nickname || '').trim();
        if (nickname) {
          const nicknameChanged = session.get('user') !== nickname;
          session.set('user', nickname);
          if (nicknameChanged) {
            this.#refreshShellProfileHeader(nickname);
          }
        }

        if (!sessionData?.authenticated && !sessionData?.token && isLoggedShell) {
          session.clear();
          window.location.replace('/');
        }

        return sessionData;
      } catch (error) {
        // Token recusado não é uma indisponibilidade momentânea: manter o shell
        // logado sem perfil faz o cabeçalho cair em "Visitante".
        if (!this.#isTransientSessionValidationFailure(error) && isLoggedShell) {
          session.clear();
          window.location.replace('/');
        }
        return null;
      } finally {
        this.#authSyncInFlight = null;
      }
    })();

    return this.#authSyncInFlight;
  }

  #refreshShellProfileHeader(nickname) {
    const trigger = document.querySelector('.user-menu-trigger');
    const greeting = trigger?.querySelector('span:first-child');
    if (!greeting) return;

    const template = String(window?.dic?.labels?.hello || 'Olá, {name}!');
    greeting.textContent = template.replace('{name}', nickname);
  }

  #setupAuthSync(config, loggedPage, unloggedPage) {
    this.#teardownAuthSync();
    if (typeof window === 'undefined') return;

    const run = () => {
      this.#syncAuthState(config, loggedPage, unloggedPage);
    };

    this.#authSyncTimer = window.setInterval(run, 8000);
    this.#authFocusListener = run;
    this.#authVisibilityListener = () => {
      if (document.visibilityState === 'visible') run();
    };

    window.addEventListener('focus', this.#authFocusListener);
    document.addEventListener('visibilitychange', this.#authVisibilityListener);
    window[AUTH_SYNC_RUNTIME_KEY] = {
      timer: this.#authSyncTimer,
      focusListener: this.#authFocusListener,
      visibilityListener: this.#authVisibilityListener,
    };
  }

  async #settleShellTransition() {
    await new Promise((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  /* ===== Tema ===== */
  /**
   * Aplica classe CSS do tema ativo no `body`.
   * @private
   * @param {string} theme Tema alvo.
   * @returns {void}
   */
  #applyThemeClass(theme) {
    const themes = ['dark', 'light', 'system', 'notFound'];

    if (theme === 'notFound') {
      document.body.classList.remove(...themes);
      document.body.classList.add('notFound');
      return;
    }

    document.body.classList.remove('notFound');
    applyResolvedVisualTheme(theme);
  }

  #applyThemeVariants(config) {
    const themeSettings = resolveThemeSettings(config);
    const lightName = normalizeThemeName(themeSettings.lightName, 'light/blue-goldenrod-01');
    const darkName = normalizeThemeName(themeSettings.darkName, 'dark/gray-goldenrod-01');
    const useExternalThemeCss = String(process.env.THEME_EXTERNAL_CSS || '0') !== '0';
    const ensureThemeLink = (id, href) => {
      let link = document.getElementById(id);
      if (!link) {
        link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.type = 'text/css';
        document.head.appendChild(link);
      }
      if (link.getAttribute('href') !== href) {
        link.setAttribute('href', href);
      }
    };

    if (useExternalThemeCss) {
      ensureThemeLink('theme-light-variant', `/styles/themes/${lightName}.css`);
      ensureThemeLink('theme-dark-variant', `/styles/themes/${darkName}.css`);
    } else {
      document.getElementById('theme-light-variant')?.remove();
      document.getElementById('theme-dark-variant')?.remove();
    }
  }

  /* ===== Dicionarios ===== */
  /**
   * Carrega dicionários do idioma informado para `window.dic`.
   * @public
   * @param {string|null} lang Idioma alvo.
   * @returns {Promise<void>}
   */
  async loadDictionaries(lang) {
    const path = window.location.pathname;
    const tempLang = path.split('/')[1];
    const language = window?.language && typeof window.language === 'object'
      ? window.language
      : await ajax.getParam('language', { loader: false });
    const defaultLang = normalizeLocale(language?.default) || DEFAULT_LOCALE;
    const supportedLangs = normalizeSupportedLocales(language?.supported, defaultLang);
    const routeLang = normalizeLocale(tempLang);
    const lang2 = supportedLangs.includes(routeLang) ? routeLang : defaultLang;


    lang = normalizeLocale(lang) || normalizeLocale(session.get('language')) || lang2;
    if (!supportedLangs.includes(lang)) {
      lang = defaultLang;
    }
    if (typeof window.dic !== 'object') {
      window.dic = {};
    }

    const coreModules = CORE_DICTIONARY_MODULES[lang] || {};
    const productModules = PRODUCT_DICTIONARY_MODULES[lang] || {};
    const filenames = new Set([...Object.keys(coreModules), ...Object.keys(productModules)]);

    if (filenames.size === 0) {
      return;
    }

    const resolveModulePayload = (loadedModule) => {
      if (!loadedModule || typeof loadedModule !== 'object') {
        return loadedModule;
      }
      if (loadedModule.__esModule && Object.prototype.hasOwnProperty.call(loadedModule, 'default')) {
        return loadedModule.default;
      }
      return Object.prototype.hasOwnProperty.call(loadedModule, 'default')
        ? loadedModule.default
        : loadedModule;
    };

    for (const filename of filenames) {
      const coreData = coreModules[filename]
        ? resolveModulePayload(await coreModules[filename]())
        : undefined;
      const productData = productModules[filename]
        ? resolveModulePayload(await productModules[filename]())
        : undefined;

      window.dic[filename] = mergeDictionaryValues(coreData, productData);
    }
  }

  /* ===== Paginas ===== */
  /**
   * Renderiza página 404.
   * @private
   * @returns {Promise<void>}
   */
  async #notFound() {
    // `notFound` é estado de layout, não um tema. Preserve a variante visual
    // ativa para que toda a página 404 permaneça dark ou light por inteiro.
    await this.setTheme();
    await renderPlatformNotFound(window.dic, '/');
  }

  /**
   * Valida e persiste página alvo na sessão.
   * @private
   * @param {string} page Página solicitada.
   * @returns {Promise<boolean>}
   */
  async #setPage(page) {
    if (page === '404') {
      return false;
    }

    if (!Object.prototype.hasOwnProperty.call(PAGE_LOADERS, page)) {
      document.body.innerHTML = '';
      await this.#notFound();
      return false;
    }

    session.set('page', page);
    return true;
  }

  /**
   * Normaliza o slug informado na URL.
   * @private
   * @param {string|null} rawSlug Slug bruto.
   * @returns {string|null}
   */
  #normalizeRouteSlug(rawSlug) {
    if (typeof rawSlug !== 'string') return null;

    let decoded = rawSlug;
    try {
      decoded = decodeURIComponent(rawSlug);
    } catch (_) {
      decoded = rawSlug;
    }

    const cleaned = decoded
      .trim()
      .replace(/^\/+|\/+$/g, '')
      .toLowerCase();

    if (!cleaned || cleaned === 'null' || cleaned === 'undefined') {
      return null;
    }

    return cleaned;
  }

  /**
   * Inicializa contexto da aplicação e resolve idioma, tema e página.
   * @public
   * @param {string|null} [lang=null] Idioma inicial opcional.
   * @param {string|null} [page=null] Página inicial opcional.
   * @param {string|null} [theme=null] Tema inicial opcional.
   * @returns {Promise<void>}
   */
  async initializePage(lang = null, page = null, theme = null) {
    const isFirstRender = !this.#hasCompletedFirstRender;
    if (this.#keyboardShortcutListener) {
      document.removeEventListener('keydown', this.#keyboardShortcutListener);
    }

    this.#keyboardShortcutListener = function (e) {
      const rawKey = typeof e?.key === 'string' ? e.key : '';
      if (!rawKey) return;

      let key = rawKey.toLowerCase(); // "enter", "escape", "f12" etc.
      if (key === 'escape') key = 'esc'; // normaliza para combinar com data-button="esc"

      if (key === 'enter') {
        const target = e.target;
        const acceptsLineBreaks = target?.tagName === 'TEXTAREA'
          || target?.isContentEditable === true
          || Boolean(target?.closest?.(
            '.ql-editor, .cm-content, [contenteditable="true"], [contenteditable="plaintext-only"]'
          ));
        if (e.isComposing || acceptsLineBreaks) return;
      }

      const isVisible = (el) => {
        if (!el || el.disabled) return false;
        if (el.offsetParent === null) return false;
        return true;
      };

      const getZIndex = (el) => {
        const dialog = el.closest('.dialog');
        const target = dialog || el;
        const z = Number.parseInt(window.getComputedStyle(target).zIndex, 10);
        return Number.isFinite(z) ? z : 0;
      };

      const dialogStack = Array.from(document.querySelectorAll('.dialog.show'))
        .filter(isVisible)
        .sort((a, b) => getZIndex(a) - getZIndex(b));
      const topDialog = dialogStack.at(-1) || null;

      const escapedKey = (globalThis.CSS && typeof globalThis.CSS.escape === 'function')
        ? globalThis.CSS.escape(key)
        : String(key || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      let candidates = Array.from(document.querySelectorAll(`[data-button="${escapedKey}"]`))
        .filter(isVisible);

      if (topDialog) {
        const topCandidates = candidates.filter((btn) => topDialog.contains(btn));
        if (topCandidates.length === 0) {
          // Com modal aberto, nunca delega Enter/Esc para elementos por trás.
          e.preventDefault();
          return;
        }
        candidates = topCandidates;
      }

      if (candidates.length === 0) return;

      candidates.sort((a, b) => getZIndex(a) - getZIndex(b));
      const btn = candidates.at(-1);
      if (!btn) return;

      e.preventDefault();
      btn.click();
    };

    document.addEventListener('keydown', this.#keyboardShortcutListener);

    const initialRequestedPage = typeof page === 'string' ? page.trim() : page;
    const config = getAppConfig() || await ajax.getParam(undefined, { loader: false });

    if (typeof window !== 'undefined') {
      if (isValidAppConfig(config)) {
        window.config = config;
      }
      window.epGoToProduct = this.goToProduct.bind(this);
    }
    this.#applyThemeVariants(config);

    this.#consumeRedirectAuthToken();

    this.#cleanSsoGrantFromUrl();
    const hasSsoProbeFlag = false;

    if (initialRequestedPage === '404') {
      const requestedLang = normalizeLocale(lang) || normalizeLocale(session.get('language')) || DEFAULT_LOCALE;
      const fallbackLang = NOT_FOUND_FALLBACK_LABELS[requestedLang] ? requestedLang : 'pt-BR';

      document.documentElement.lang = fallbackLang;

      document.body.classList.remove('route-switching', 'lgd', 'uld');
      document.body.classList.add('uld');

      window.dic = {
        lang: fallbackLang,
        labels: NOT_FOUND_FALLBACK_LABELS[fallbackLang],
      };
      session.set('language', fallbackLang);

      await renderPlatformNotFound(window.dic, '/');
      this.#hasCompletedFirstRender = true;
      return;
    }

    const themeSettings = resolveThemeSettings(config);
    const defaultConfigLang = normalizeLocale(config?.language?.default) || DEFAULT_LOCALE;
    const configPage = config?.page && typeof config.page === 'object' ? config.page : {};
    const loggedPage = typeof configPage.logged === 'string' && configPage.logged.trim()
      ? configPage.logged
      : 'logged';
    const unloggedPage = typeof configPage.unlogged === 'string' && configPage.unlogged.trim()
      ? configPage.unlogged
      : 'unlogged';
    const initialActiveLang = lang || (session.get('language') || defaultConfigLang);
    const requestedPage = typeof page === 'string' ? page.trim() : page;
    const isExplicitUnloggedEntry = requestedPage === unloggedPage;

    const currentProduct = this.#resolveCurrentProductCode();
    let sessionData;
    let sessionValidationRejected = false;
    try {
      sessionData = currentProduct && currentProduct !== 'site'
        ? await this.#getLocalProductSessionData(currentProduct, { suppressErrors: false })
        : await authClient.getSessionData({ loader: false, retries: 3, retryDelay: 250 });
    } catch (error) {
      const preservedToken = String(session.get('token') || '').trim();
      const canPreserveLocalSession = preservedToken && this.#isTransientSessionValidationFailure(error);
      if (!canPreserveLocalSession) {
        // Uma sessão recusada precisa seguir o fluxo normal de logout abaixo.
        // Não fabricamos uma autenticação sem profile ("Olá, Visitante!").
        sessionData = null;
        sessionValidationRejected = true;
      } else {
        console.warn('Falha transitória ao validar sessão; mantendo sessão local até nova validação.');
        sessionData = {
          authenticated: true,
          token: preservedToken,
          profile: {},
          menus: [],
        };
      }
    }

    if (!sessionData?.authenticated && !sessionData?.token && !hasSsoProbeFlag && !sessionValidationRejected) {
      sessionData = currentProduct && currentProduct !== 'site'
        ? await this.#getLocalProductSessionData(currentProduct, { suppressErrors: false })
        : await authClient.getSessionData({ loader: false, retries: 3, retryDelay: 250 });
    }

    sessionData = await this.#syncDetectedTimezone(currentProduct, sessionData);

    const profile = sessionData?.profile || {};
    let isAuthenticated = !!(sessionData?.authenticated || sessionData?.token);
    const localVisualPreferences = readVisualPreferences();
    if (!isAuthenticated) {
      const persistedMfaDeviceId = storage.get('mfaDeviceId');
      session.set('user', null);
      session.set('userPhotoURL', userDefaultPhoto);
      session.set('token', null);
      storage.clear();
      if (persistedMfaDeviceId) {
        storage.set('mfaDeviceId', persistedMfaDeviceId);
      }
      this.#writeEcosystemAuthState('unauthenticated');

    }
    const userPhotoURL = resolveUserPhotoURL(profile?.photoURL);
    if (isAuthenticated) {
      this.#writeEcosystemAuthState('authenticated');
    }
    const routingConfig = resolveRoutingConfig(config);
    const publicRouteSlugs = collectPublicRouteSlugs(routingConfig);
    const activeLang = lang || profile.language || (session.get('language') || defaultConfigLang);
    let resolvedRequestedPage = requestedPage;
    let routeSlug = null;

    if (resolvedRequestedPage && resolvedRequestedPage !== '404' && !Object.prototype.hasOwnProperty.call(PAGE_LOADERS, resolvedRequestedPage)) {
      routeSlug = this.#normalizeRouteSlug(resolvedRequestedPage);
      if (routeSlug) {
        if (!isAuthenticated && !publicRouteSlugs.has(routeSlug)) {
          resolvedRequestedPage = '404';
          routeSlug = null;
        } else if (isAuthenticated) {
          resolvedRequestedPage = null;
        } else {
          resolvedRequestedPage = null;
        }
      } else {
        resolvedRequestedPage = '404';
      }
    }

    if (!isAuthenticated && resolvedRequestedPage === loggedPage) {
      routeSlug = this.#normalizeRouteSlug(resolvedRequestedPage) || routeSlug;
      resolvedRequestedPage = null;
    }

    const configuredTheme = themeSettings.mode;
    const allowSessionTheme = themeSettings.canChange;
    const persistedTheme = allowSessionTheme ? session.get('theme') : null;
    const profileTheme = allowSessionTheme && isAuthenticated ? normalizeThemeToken(profile?.theme) : null;
    const requestedTheme = normalizeThemeToken(theme);
    const preferredTheme = requestedTheme
      || profileTheme
      || normalizeThemeToken(localVisualPreferences.theme)
      || normalizeThemeToken(persistedTheme)
      || configuredTheme;
    const activeTheme = allowSessionTheme ? preferredTheme : configuredTheme;
    const activeScale = isAuthenticated
      ? normalizeVisualScale(profile?.scale)
      : normalizeVisualScale(localVisualPreferences.scale);
    const activePage = resolvedRequestedPage || (isAuthenticated ? loggedPage : unloggedPage);

    if (activePage === '404') {
      document.body.classList.remove('route-switching');
    } else {
      document.body.classList.add('route-switching');
    }

    document.body.classList.remove('lgd', 'uld');

    if (isAuthenticated) {
      document.body.classList.add('lgd');
    } else {
      document.body.classList.add('uld');
    }

    session.set('user', profile.nickname || null);
    session.set('userPhotoURL', userPhotoURL);
    if (sessionData?.token) {
      session.set('token', sessionData.token);
    } else if (!isAuthenticated) {
      session.set('token', null);
    }
    if (allowSessionTheme && profileTheme) {
      session.set('theme', profileTheme);
    } else if (activeTheme && activeTheme !== 'notFound') {
      if (allowSessionTheme) {
        session.set('theme', activeTheme);
      }
    }
    session.set('scale', activeScale);
    if (profile?.timezone) {
      session.set('timezone', profile.timezone);
    }

    if (routeSlug) {
      session.set('routeSlug', routeSlug);
    } else {
      session.delete('routeSlug');
    }

    await this.setLanguage(activeLang);
    if (activeTheme !== 'notFound') {
      applyVisualPreferences(
        { theme: activeTheme, scale: activeScale },
        { persist: true }
      );
    }
    await this.setTheme(activeTheme);
    await this.loadPage(activePage);
    this.#setupAuthSync(config, loggedPage, unloggedPage);
    this.#hasCompletedFirstRender = true;
  }

  /**
   * Carrega módulo da página informada.
   * @public
   * @param {string} page Página alvo.
   * @returns {Promise<void>}
   */
  async loadPage(page) {
    if (await this.#setPage(page)) {
      const initialRouteSlug = session.get('routeSlug');
      const shouldSkipShellLoader = page === 'unlogged' && typeof initialRouteSlug === 'string' && initialRouteSlug.trim().length > 0;
      const shouldSettleTransition = document.body.classList.contains('route-switching');
      const loader = shouldSkipShellLoader ? null : new Loader();
      loader?.start();
      try {
        document.querySelectorAll('.dialog, .overlay, .user-menu-backdrop, .module-aside-cart').forEach((el) => el.remove());
        document.querySelector('header')?.empty?.();
        document.querySelector('aside')?.empty?.();
        document.querySelector('main')?.empty?.();
        document.querySelector('footer')?.empty?.();
        const loadPageModule = PAGE_LOADERS[page];
        const module = loadPageModule ? await loadPageModule() : null;

        const pageRunner = typeof module === 'function'
          ? module
          : module?.default;
        if (typeof pageRunner === 'function') {
          await pageRunner();
        }

        if (shouldSettleTransition) {
          await this.#settleShellTransition();
        }
      } finally {
        loader?.end();
        document.body.classList.remove('route-switching');
      }
    } else if (page === '404') {
      document.body.classList.remove('route-switching');
      await this.#notFound();
    }
  }

  /**
   * Define idioma atual da aplicação e garante dicionários carregados.
   * @public
   * @param {string} lang Idioma alvo.
   * @returns {Promise<void>}
   */
  async setLanguage(lang) {
    window.language = await ajax.getParam('language', { loader: false });
    const languageConfig = window.language && typeof window.language === 'object'
      ? window.language
      : {};
    const defaultLang = normalizeLocale(languageConfig?.default) || DEFAULT_LOCALE;
    const supportedLangs = normalizeSupportedLocales(languageConfig?.supported, defaultLang);

    lang = normalizeLocale(lang);
    if (!supportedLangs.includes(lang)) {
      lang = defaultLang;
    }

    session.set('language', lang);
    document.documentElement.lang = lang;

    if (!window.dic || typeof window.dic !== 'object') {
      window.dic = {};
    }

    if (!window.dic.labels || window.dic.lang !== lang) {
      window.dic = {};
      await this.loadDictionaries(lang);
      window.dic.lang = lang;
    }

  }

  /**
   * Define tema visual da aplicação.
   * @public
   * @param {string} theme Tema alvo.
   * @returns {Promise<void>}
   */
  async setTheme(theme) {
    const config = getAppConfig() || await ajax.getParam(undefined, { loader: false });
    this.#applyThemeVariants(config);
    const themes = ['dark', 'light', 'system', 'notFound'];
    const themeSettings = resolveThemeSettings(config);
    const configuredTheme = themeSettings.mode;
    const allowSessionTheme = themeSettings.canChange;
    const sessionTheme = allowSessionTheme ? session.get('theme') : null;

    let resolvedTheme = configuredTheme;

    if (theme === 'notFound') {
      resolvedTheme = 'notFound';
    } else if (allowSessionTheme) {
      if (themes.includes(theme)) {
        resolvedTheme = theme;
      } else if (themes.includes(sessionTheme)) {
        resolvedTheme = sessionTheme;
      } else {
        resolvedTheme = configuredTheme;
      }
    } else {
      resolvedTheme = configuredTheme;
    }

    resolvedTheme = normalizeThemeToken(resolvedTheme) || (resolvedTheme === 'notFound' ? 'notFound' : configuredTheme);

    if (resolvedTheme === 'system') {
      if (allowSessionTheme) {
        session.set('theme', 'system');
        applyVisualPreferences({
          theme: 'system',
          scale: normalizeVisualScale(session.get('scale')),
        }, { persist: true });
      } else {
        applyVisualPreferences({
          theme: 'system',
          scale: normalizeVisualScale(session.get('scale')),
        });
      }
      return;
    }

    this.#applyThemeClass(resolvedTheme);
    if (allowSessionTheme && resolvedTheme !== 'notFound') {
      session.set('theme', resolvedTheme);
      applyVisualPreferences({
        theme: resolvedTheme,
        scale: normalizeVisualScale(session.get('scale')),
      }, { persist: true });
    }
  }
}
